import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, forwardRef, ViewChild, effect, input, signal, output, Injector, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
 
@Component({
  selector: 'app-editable-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './editable-select.component.html',
  styleUrls: ['./editable-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditableSelectComponent),
      multi: true
    }
  ]
})
export class EditableSelectComponent implements ControlValueAccessor {
  @ViewChild('inputElement') inputElement?: ElementRef<HTMLInputElement>;
 
  readonly placeholder = input<string>('');
  readonly options = input<any[]>([]);
  readonly displayPath = input<string>();
  readonly minHeight = input<number>(24);
  readonly editMode = input<boolean>(false);
  readonly autocomplete = input<boolean>(false);
  readonly allowIndividualEditMode = input<boolean>(false);
  readonly allowCustomValue = input<boolean>(false);
  readonly displayValueClicked = output<any>();

  public isEditMode = signal(false);
 
  public showEditButton: boolean = false;
  public disabled: boolean = false;
  public searchText: string = '';
  public filteredOptions: any[] = [];
  public highlightedIndex: number = -1;
  public showDropdown: boolean = false;
 
  // Custom select dropdown state
  public showSelectDropdown: boolean = false;
  public selectHighlightedIndex: number = -1;
 
  onChange: any = () => {};
  onTouched: any = () => {};

  private scrollListener = () => this.positionSelectDropdown();
  private resizeListener = () => this.positionSelectDropdown();
 
  //private _value: any;
  private _originalValue: any;

  private _valueSignal = signal<any>(null);

  private ngControl: NgControl | null = null;
 
  constructor(private elementRef: ElementRef, private injector:Injector) {
    effect(() => {
      if (this.editMode() && !this.isEditMode()) this.enterEditMode();
      else if (!this.editMode() && this.isEditMode()) this.exitEditMode(true); //false was set before, but the editModeSignal change shall not set false to avoid unnecessary triggeres when reverting back and forth the current selected value.
    });
    effect(() => {
      const opts = this.options();
      // Only validate after options have genuinely loaded (non-empty array)
      if (opts?.length > 0 && this._valueSignal() && !this.allowCustomValue()) {
        const stillValid = opts.find(opt => this.compareOptions(opt, this._valueSignal()));
        if (!stillValid) this.clearInvalidValueSilently();
      }
    });
  }

  ngAfterViewInit(): void {
    //this whole block was created due to the need that 2 controls bind to the same form and need to get updated synchronously.
    //For anyone reading this later (including future-you):
    //a custom ControlValueAccessor's writeValue isn't called for updates that originate from another accessor bound to the same FormControl.
    //Angular skips it via emitModelToViewChange: false. 
    //So for true multi-instance sync, you need to subscribe to the control's valueChanges directly (via NgControl, set up in ngAfterViewInit to avoid the init-order race), 
    //rather than relying on writeValue alone.
    this.ngControl = this.injector.get(NgControl, null, { optional: true, self: true });
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
      this.ngControl.control?.valueChanges.subscribe(value => {
        if (!this.compareOptions(value, this._valueSignal())) {
          this._valueSignal.set(value);
          if (this.autocomplete()) {
            this.searchText = this.getDisplayValue(value);
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.removeDropdownListeners();
  }
 
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      if (this.showSelectDropdown) this.showSelectDropdown = false;
      if (this.allowIndividualEditMode() && this.isEditMode()) this.exitEditMode(true);
    }
  }
 
  get value(): any {
    return this._valueSignal();
  }

  private clearInvalidValueSilently(): void {
    this._valueSignal.set(null);
    // Updates the control's value/validity, but doesn't emit valueChanges.
    // Other accessors bound to the same control still get writeValue(null).
    this.ngControl?.control?.setValue(null, { emitEvent: false });
  }

  public onDisplayValueClicked(): void {
    this.displayValueClicked.emit(this._valueSignal());
  }

  public onBlur(): void {
    this.onTouched();
    if (this.allowIndividualEditMode()) return; // exitEditMode is handled by document click / keyboard
    this.exitEditMode(true);
  }

  public onFocus(): void {
    this.filterOptions();
    this.showDropdown = true;
    this.positionDropdown();
  }
 
  public toggleSelectDropdown(): void {
    if (this.disabled) return;
    this.showSelectDropdown = !this.showSelectDropdown;
    if (this.showSelectDropdown) {
      setTimeout(() => {
        this.positionSelectDropdown();
        const dropdown = this.elementRef.nativeElement.querySelector('.select-dropdown');
        if (dropdown) dropdown.style.visibility = 'visible';
      });
      window.addEventListener('scroll', this.scrollListener, true); // capture = true
      window.addEventListener('resize', this.resizeListener);
    }
    else {
      this.removeDropdownListeners();
    }
  }

  private removeDropdownListeners(): void {
    window.removeEventListener('scroll', this.scrollListener, true);
    window.removeEventListener('resize', this.resizeListener);
  }

 
  private positionSelectDropdown(): void {
    const trigger = this.elementRef.nativeElement.querySelector('.select-trigger');
    const dropdown = this.elementRef.nativeElement.querySelector('.select-dropdown');
    if (!trigger || !dropdown) return;
    //dropdown.style.visibility = 'hidden';
    const rect: DOMRect = trigger.getBoundingClientRect();
    dropdown.style.top = `${rect.bottom}px`;
    dropdown.style.left = `${rect.left}px`;
    dropdown.style.width = `${rect.width}px`;
  }
 
  public selectCustomOption(option: any): void {
    this._valueSignal.set(option);
    this.onChange(this._valueSignal());
    this.showSelectDropdown = false;
    this.selectHighlightedIndex = -1;
    if (this.allowIndividualEditMode()) this.exitEditMode(true);
  }
 
  public onSelectChange(newValue: any): void {
    if (this._valueSignal() !== newValue) {
      this._valueSignal.set(newValue);
      this.onChange(this._valueSignal());
    }
    this.showSelectDropdown = false;
  }
 
  get selectedOption(): any {
    return this.options()?.find(opt => this.compareOptions(opt, this.value));
  }
 
  get displayValue(): string {
    const option = this.selectedOption;
    if (option) return this.getDisplayValue(option);
    if (this._valueSignal()) return this.getDisplayValue(this._valueSignal());
    return '';
  }
 
  public validValue(value: any) {
    return this.options()?.find(opt => this.compareOptions(opt, value));
  }
 
  public toggleEditButton(value: boolean) {
    this.showEditButton = value;
  }
 
  public compareOptions(opt1: any, opt2: any): boolean {
    if (opt1?.id !== undefined && opt2?.id !== undefined) {
      return opt1.id === opt2.id;
    }
    if (this.displayPath()) {
      return opt1?.[this.displayPath()!] === opt2?.[this.displayPath()!];
    }
    return opt1 === opt2;
  }
 
  public isSelected(option: any): boolean {
    return this.compareOptions(option, this.value);
  }
 
  public enterEditMode(): void {
    this._originalValue = this._valueSignal() ?? null;
    setTimeout(() => {
      this.isEditMode.set(true);
 
      if (this.autocomplete()) {
        this.searchText = this.getDisplayValue(this._valueSignal());
        this.filterOptions();
        if (this.allowIndividualEditMode()) {
          this.showDropdown = true;
          setTimeout(() => {
            if (this.inputElement) {
              this.inputElement.nativeElement.focus();
              this.inputElement.nativeElement.select();
              this.positionDropdown();
            }
          }, 0);
        }
      } else if (this.allowIndividualEditMode()) {
        setTimeout(() => this.toggleSelectDropdown(), 0);
      }
    }, 0);
  }
 
  public exitEditMode(save: boolean): void {
    if (!save) {
      const valueChanged = (this._valueSignal() ?? null) !== (this._originalValue ?? null);
      this._valueSignal.set(this._originalValue);
      if (valueChanged) this.onChange(this._valueSignal());
    } else if (this.autocomplete()) {
      if (!this.searchText) {
        this._valueSignal.set(null);
        this.onChange(null);
      } else {
        const matchedOption = this.filteredOptions.find(opt =>
          this.getDisplayValue(opt).toLowerCase() === this.searchText.toLowerCase()
        );
        if (matchedOption) {
          this._valueSignal.set(matchedOption);
          this.onChange(this._valueSignal());
        } else if (this.allowCustomValue()) {
          const newValue = this.displayPath()
            ? { [this.displayPath()!]: this.searchText }
            : this.searchText;
          this._valueSignal.set(newValue);
          this.onChange(this._valueSignal());
        }
      }
    }
 
    this.isEditMode.set(false);
    this.showDropdown = false;
    this.showSelectDropdown = false;
    this.searchText = '';
    this.highlightedIndex = -1;
    this.selectHighlightedIndex = -1;
  }
 
  public onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchText = target.value;
    this.filterOptions();
    this.showDropdown = true;
    this.highlightedIndex = -1;
    this.positionDropdown();
  }
 
  public filterOptions(): void {
    if (!this.searchText) {
      this.filteredOptions = [...this.options()];
      return;
    }
    const searchLower = this.searchText.toLowerCase();
    this.filteredOptions = this.options().filter(option =>
      this.getDisplayValue(option).toLowerCase().includes(searchLower)
    );
  }
 
  public getDisplayValue(option: any): string {
    if (!option) return '';
    if (this.displayPath()) return option[this.displayPath()!] || '';
    return option?.toString() || '';
  }
 
  public selectOption(option: any): void {
    this._valueSignal.set(option);
    this.onChange(this._valueSignal());
    this.searchText = this.getDisplayValue(option);
    this.showDropdown = false;
    if (this.allowIndividualEditMode()) this.exitEditMode(true);
  }
 
  public onKeyDown(event: KeyboardEvent): void {
    if (this.autocomplete() && this.showDropdown) {
      this.handleAutocompleteKeydown(event);
    } else if (!this.autocomplete() && this.showSelectDropdown) {
      this.handleCustomSelectKeydown(event);
    } else {
      this.handleSelectKeydown(event);
    }
  }
 
  public handleAutocompleteKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredOptions.length - 1);
        this.scrollToHighlighted();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
        this.scrollToHighlighted();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredOptions.length) {
          this.selectOption(this.filteredOptions[this.highlightedIndex]);
        } else if (this.filteredOptions.length === 1) {
          this.selectOption(this.filteredOptions[0]);
        } else if (this.allowIndividualEditMode()) {
          this.exitEditMode(true);
        }
        break;
      case 'Escape':
        event.preventDefault();
        if (this.allowIndividualEditMode()) this.exitEditMode(false);
        break;
      case 'Tab':
        if (this.highlightedIndex >= 0) {
          event.preventDefault();
          this.selectOption(this.filteredOptions[this.highlightedIndex]);
        } else if (this.allowIndividualEditMode()) {
          this.exitEditMode(true);
        }
        break;
    }
  }
 
  public handleCustomSelectKeydown(event: KeyboardEvent): void {
    const opts = this.options() as any[];
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectHighlightedIndex = Math.min(this.selectHighlightedIndex + 1, opts.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectHighlightedIndex = Math.max(this.selectHighlightedIndex - 1, 0);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectHighlightedIndex >= 0) {
          this.selectCustomOption(opts[this.selectHighlightedIndex]);
        } else {
          this.showSelectDropdown = false;
          if (this.allowIndividualEditMode()) this.exitEditMode(true);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.showSelectDropdown = false;
        if (this.allowIndividualEditMode()) this.exitEditMode(false);
        break;
    }
  }
 
  handleSelectKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.allowIndividualEditMode()) this.exitEditMode(true);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (this.allowIndividualEditMode()) this.exitEditMode(false);
    }
  }
 
  scrollToHighlighted(): void {
    setTimeout(() => {
      const dropdown = this.elementRef.nativeElement.querySelector('.autocomplete-dropdown');
      const highlighted = dropdown?.querySelector('.option.highlighted');
      if (highlighted && dropdown) {
        highlighted.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  }
 
  positionDropdown(): void {
    setTimeout(() => {
      const inputEl = this.inputElement?.nativeElement;
      const dropdown = this.elementRef.nativeElement.querySelector('.autocomplete-dropdown');
      if (inputEl && dropdown) {
        const rect = inputEl.getBoundingClientRect();
        dropdown.style.top = `${rect.bottom}px`;
        dropdown.style.left = `${rect.left}px`;
        dropdown.style.width = `${rect.width}px`;
        dropdown.style.visibility = 'visible';
      }
    }, 0);
  }
 
  writeValue(value: any): void {
    this._valueSignal.set(value);
    if (this.autocomplete()) {
      this.searchText = this.getDisplayValue(value);
    }
  }
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }
}